// Pure builders for the `client_success` docs the client page writes. No Firebase imports:
// the caller adds the timestamps and passes `deleteField()` in as `removeField`.
import { LEGACY_PLANNED_COMMENT } from "../../../shared/success/successRecords";

export interface SuccessMetricForm {
  goal: string;
  goal_update: string;
  goal_achieved: boolean;
  target1: string;
  target2: string;
  target3: string;
  target1_update: string;
  target2_update: string;
  target3_update: string;
  target1_achieved: boolean;
  target2_achieved: boolean;
  target3_achieved: boolean;
  comments: string;
}

export interface SuccessMetricAuthor {
  created_by: string;
  created_by_uid: string;
  created_by_email: string;
}

export interface AssessmentTargetSuggestion {
  target1: string;
  target2: string;
  target3: string;
}

// The legacy placeholder text is never written back, whatever the record's origin.
function cleanComment(comments: string): string {
  return comments === LEGACY_PLANNED_COMMENT ? "" : comments;
}

// Fields are copied one by one so a caller's extra properties never reach Firestore.
function authorFields(author: SuccessMetricAuthor): SuccessMetricAuthor {
  return {
    created_by: author.created_by,
    created_by_uid: author.created_by_uid,
    created_by_email: author.created_by_email,
  };
}

export function buildUpdatePayload(clientId: string, form: SuccessMetricForm, author: SuccessMetricAuthor): Record<string, unknown> {
  return {
    client_id: clientId,
    goal: form.goal,
    goal_update: form.goal_update,
    goal_achieved: form.goal_achieved,
    target1: form.target1,
    target2: form.target2,
    target3: form.target3,
    target1_update: form.target1_update,
    target2_update: form.target2_update,
    target3_update: form.target3_update,
    target1_achieved: form.target1_achieved,
    target2_achieved: form.target2_achieved,
    target3_achieved: form.target3_achieved,
    comments: cleanComment(form.comments),
    ...authorFields(author),
    kind: "update",
  };
}

// Filling a placeholder or assessment seed: write it as an update and drop the stored seed `id` field.
export function buildFillPayload(
  clientId: string,
  form: SuccessMetricForm,
  author: SuccessMetricAuthor,
  removeField: unknown
): Record<string, unknown> {
  return { ...buildUpdatePayload(clientId, form, author), id: removeField };
}

export function buildPlannedPayload(
  clientId: string,
  next: { goal: string; target1: string; target2: string; target3: string },
  author: SuccessMetricAuthor
): Record<string, unknown> {
  return {
    client_id: clientId,
    goal: next.goal,
    target1: next.target1,
    target2: next.target2,
    target3: next.target3,
    goal_update: "",
    target1_update: "",
    target2_update: "",
    target3_update: "",
    goal_achieved: false,
    target1_achieved: false,
    target2_achieved: false,
    target3_achieved: false,
    comments: "",
    ...authorFields(author),
    kind: "planned",
  };
}

function nonBlank(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

// In `id` order, the last non-blank value of each assessment target wins (kept untrimmed).
export function suggestTargetsFromAssessments(docs: Array<{ id: string; data: Record<string, unknown> }>): AssessmentTargetSuggestion {
  const suggestion: AssessmentTargetSuggestion = { target1: "", target2: "", target3: "" };
  const sorted = docs.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  sorted.forEach(({ data }) => {
    if (nonBlank(data.nutrition_target)) suggestion.target1 = data.nutrition_target;
    if (nonBlank(data.training_target_1)) suggestion.target2 = data.training_target_1;
    if (nonBlank(data.training_target_2)) suggestion.target3 = data.training_target_2;
  });
  return suggestion;
}
